import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useUserContext } from "../../../contexts/UserContext";
import { useContentfulContext } from "../../../contexts/ContentfulContext";
import { deleteSpecificTealiumProduct } from "../../../utils/APIs";
import AgGridWrapper from "../../UI/AgGrid";
import ConfirmModal from "../../UI/Modal/confirmModal";
import PrimaryModal from "../../UI/Modal/PrimaryModal";
import { PrimaryButton, ErrorButton } from "../../UI";

const DeleteTealiumIntegrationModal = ({
    setDeleteIntegration,
    id,
    setActionLoading,
    getTealiumDataHandler,
}) => {
    const { userData } = useUserContext();
    const { content } = useContentfulContext();

    const modalContent = content.DELETE_TEALIUM_INTEGRATION_MODAL;

    const [dependencies, setDependencies] = useState(null);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [showGrid, setShowGrid] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        const fetchDependencies = async () => {
            try {
                setLoading(true);
                const result = await deleteSpecificTealiumProduct(
                    userData.token,
                    userData.selectedOrganization.org_token,
                    id,
                    true // checkDependencies = true
                );
                if (result?.data && Object.keys(result.data).length > 0) {
                    setDependencies(result.data.data);
                    setMessage(result.data.message);
                }
            } catch (err) {
                setError(err?.message || modalContent.errors.checkFailed);
            } finally {
                setLoading(false);
            }
        };
        fetchDependencies();
    }, [id, userData, modalContent]);

    const handleDelete = async () => {
        try {
            setActionLoading && setActionLoading(true);
            setIsDeleting(true);
            const result = await deleteSpecificTealiumProduct(
                userData.token,
                userData.selectedOrganization.org_token,
                id
            );
            if (result) {
                const updateDefault =
                    userData?.defaultTealiumKey?._id === id ? true : false;
                await getTealiumDataHandler(updateDefault);

                setDeleteIntegration(false);
                toast.success(modalContent.delete_success_msg);
            }
        } catch (err) {
            setError(err?.message || modalContent.errors.deleteFailed);
        } finally {
            setActionLoading && setActionLoading(false);
            setIsDeleting(false);
            setShowConfirm(false);
        }
    };

    // flatten deps for grid
    const rowData = useMemo(() => {
        const rows = [];
        if (dependencies?.saved_tasks) {
            rows.push(
                ...dependencies.saved_tasks.map(d => ({
                    type: "Saved Task",
                    name: d.saved_task_name,
                    id: d.function_id,
                    ...d,
                }))
            );
        }
        if (dependencies?.experience_offer_mappings) {
            rows.push(
                ...dependencies.experience_offer_mappings.map(d => ({
                    type: "Experience Mapping",
                    name: d.experience_name,
                    id: d.records.map(r => r.id).join(", "),
                    ...d,
                }))
            );
        }
        return rows;
    }, [dependencies]);

    const colDefs = [
        { headerName: modalContent.tableHeaders.type, field: "type", flex: 1 },
        { headerName: modalContent.tableHeaders.name, field: "name", flex: 2 },
        { headerName: modalContent.tableHeaders.id, field: "id", flex: 2 },
    ];

    const autoSizeStrategy = useMemo(() => {
        return {
            type: window.innerWidth < 1024 ? "fitCellContents" : "fitGridWidth",
            defaultMinWidth: 40,
        };
    }, []);

    return (
        <>
            <PrimaryModal
                title={modalContent.reviewDependencyTitle}
                hideModal={setDeleteIntegration}
            >
                <div className="p-6 space-y-6">
                    {loading ? (
                        <p className="text-center text-gray-500">
                            {modalContent.checkingDependencies}
                        </p>
                    ) : (
                        <>
                            {!showGrid && (
                                <>
                                    {message ? (
                                        <p className="text-base text-black mb-4">
                                            {message}
                                            <br />
                                            <span className="font-semibold text-red-500">
                                                {modalContent.warning}
                                            </span>
                                        </p>
                                    ) : (
                                        <p className="text-base text-black mb-4">
                                            {modalContent.description_1}
                                        </p>
                                    )}

                                    {error && <p className="text-red-500">{error}</p>}

                                    <div className="mb-4 flex flex-col md:flex-row justify-end gap-4">
                                        {message ? (
                                            <>
                                                <PrimaryButton handleClick={() => setShowGrid(true)} className="w-full md:w-auto">
                                                    {modalContent.reviewDependencies}
                                                </PrimaryButton>
                                                <ErrorButton handleClick={() => setShowConfirm(true)} className="w-full md:w-auto">
                                                    {modalContent.formLabels.deactivate_integration_cta}
                                                </ErrorButton>
                                            </>
                                        ) : (
                                            <ErrorButton loading={isDeleting} handleClick={handleDelete} className="w-full md:w-auto">
                                                {modalContent.formLabels.deactivate_integration_cta}
                                            </ErrorButton>
                                        )}
                                    </div>
                                </>
                            )}

                            {showGrid && (
                                <div>
                                    <p className="mb-2 font-semibold">{modalContent.dependenciesPreview}</p>
                                       <div style={{ height: "300px", overflow : "auto", width:"100%" }}>
                                        <AgGridWrapper
                                            tableId="tealium-integration-dependencies-table"
                                            rowData={rowData}
                                            columnDefs={colDefs}
                                            autoSizeStrategy={autoSizeStrategy}
                                        />
                                    </div>
                                    <div className="mt-4 flex justify-end space-x-4">
                                        <PrimaryButton handleClick={() => setShowGrid(false)}>
                                            {modalContent.back}
                                        </PrimaryButton>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </PrimaryModal>

            {showConfirm && (
                <ConfirmModal
                    description={modalContent.description_1}
                    ConfirmText={modalContent.formLabels.deactivate_integration_cta}
                    error={error}
                    title={modalContent.title}
                    loading={isDeleting}
                    hideModal={setShowConfirm}
                    handleConfirmation={confirmed => {
                        if (confirmed) handleDelete();
                        else setShowConfirm(false);
                    }}
                />
            )}
        </>
    );
};

export default DeleteTealiumIntegrationModal;